import type { DocumentNode, TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import type {
  TagSelectorQuery,
  TagSelectorQueryVariables,
  TopTagsQuery,
  TopTagsQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import React, { useCallback, useContext, useEffect, useRef, useMemo, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  InputActionMeta,
  MultiValue,
  OnChangeValue,
  OptionProps,
  SelectInstance
} from 'react-select';
import { components } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { SharedComponent } from '../../../enums';
import Icons from '../../../icons';
import { IconSize } from '../../common/Icon/enums';
import Icon from '../../common/Icon/Icon';
import type { AppContextInterface } from '../../context/AppContext/AppContext';
import AppContext from '../../context/AppContext/AppContext';
import useTranslation from '../../useTranslation';
import topTagsQuery from './../TopTags.query.graphql';
import TagOption from './TagOption';
import localStyles from './TagSelector.module.pcss';
import tagSelectorQuery from './TagSelector.query.graphql';
import { useReactSelectTheme } from '../../../helpers/styles/ReactSelectThemeHelper';
import useQueryWithTracing from '../../useQueryWithTracing';
import type { Placement } from 'react-overlays/esm/usePopper';

interface Props {
  /**
   * @callback
   * A callback function called when a tag is saved.
   */
  onAdd: (tag: OnChangeValue<CreatableTagOption, boolean>, actionMeta: { action: string }) => void;

  /**
   * A callback function when the tag selector is blurred.
   *
   * @callback
   */
  onBlur?: () => void;

  /**
   * A callback function when the tag selector is focused.
   *
   * @callback
   */
  onFocus?: () => void;
  /**
   * The number of tags to return when searching.  Defaults to 5.
   */
  pageSize?: number;
  /**
   * Which query to use to fetch the tags
   */
  tagsQueryFetchType: TagsQueryFetchType;
  /**
   * List of preset tags for the message
   */
  presetTags?: TagOption[];
  /**
   * The node scope to select tags
   */
  nodeId?: string;
  /**
   * The tags to exclude from the tag selector
   */
  tagsToExclude?: TagOption[];
  /**
   * The placement of the popper element used in the tag selector.
   * Determines where the dropdown will appear relative to the input field.
   *
   * @see {@link https://popper.js.org/docs/v2/constructors/#placement} for possible placement values.
   */
  popperPlacement?: Placement;
  /**
   * Whether to exclude preset tags from the suggestions list
   */
  excludePresetTagsFromSuggestions?: boolean;
}

/**
 * Represents a creatable tag option used by react-select. This is an internal
 * property used by react-select that is currently not exposed by their TypeScript
 * definitions.
 */
export interface CreatableTagOption extends TagOption {
  /**
   * Whether the option represents the action to create a new item
   */
  __isNew__?: boolean;
}

/**
 * Enum to pass as prop so as to specify which query to use to fetch tags
 */
export enum TagsQueryFetchType {
  FETCH_PRESET_TAGS,
  FETCH_MATCHING_TAGS,
  FETCH_TOP_TAGS
}

/**
 * An selector for finding and adding tags to a message.
 *
 * @constructor
 *
 * @author Dolan Halbrook, Willi Hyde
 */
const TagSelector: React.FC<React.PropsWithChildren<Props>> = ({
  onAdd,
  onBlur,
  onFocus = () => {},
  pageSize = 5,
  tagsQueryFetchType,
  presetTags,
  nodeId,
  tagsToExclude = [],
  popperPlacement,
  excludePresetTagsFromSuggestions = false
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(SharedComponent.TAG_SELECTOR);
  const client = useApolloClient();
  const reference = useRef<SelectInstance<CreatableTagOption, boolean>>();
  const { contextNode } = useContext<AppContextInterface>(AppContext);
  const contextNodeId = nodeId === undefined ? contextNode.id : nodeId;
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  const reactSelectTheme = useReactSelectTheme();

  const useUserDefinedTags = tagsQueryFetchType === TagsQueryFetchType.FETCH_MATCHING_TAGS;

  const Option: React.FC<
    React.PropsWithChildren<OptionProps<CreatableTagOption, boolean>>
  > = props => {
    const { children, data, isFocused, selectOption } = props;

    // Fix for iOS VoiceOver: double-tap doesn't trigger onClick, so add onTouchEnd
    const handleTouchEnd = (event: React.TouchEvent) => {
      event.preventDefault();
      selectOption(data);
    };

    return (
      <components.Option
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        innerProps={{
          ...props.innerProps,
          onTouchEnd: handleTouchEnd
        }}
        className={cx(
          'lia-option',
          {
            // eslint-disable-next-line no-underscore-dangle
            'lia-option-is-create': data?.__isNew__
          },
          {
            'lia-option-is-focused': isFocused
          }
        )}
      >
        {children}
      </components.Option>
    );
  };

  /**
   * Helper function to fetch tags based on the provided query and variables.
   */
  const fetchTags = useCallback(
    async <TQuery, TVariables>(
      query: DocumentNode | TypedDocumentNode<TQuery, TVariables>,
      variables: TVariables
    ): Promise<TagOption[]> => {
      const { loading, data } = await client.query<TQuery, TVariables>({
        query,
        variables,
        fetchPolicy: 'no-cache'
      });

      if (loading) {
        return [];
      }

      if ('coreNode' in (data as TopTagsQuery)) {
        return (
          (data as TopTagsQuery)?.coreNode?.topTags.edges.map(tagNode => ({
            label: tagNode.node.text,
            value: tagNode.node.id
          })) ?? []
        );
      }

      if ('matchingTags' in (data as TagSelectorQuery)) {
        return (data as TagSelectorQuery)?.matchingTags?.map(tag => new TagOption(tag)) ?? [];
      }

      return [];
    },
    [client]
  );

  const variables: TopTagsQueryVariables = useMemo(
    () => ({
      id: contextNodeId,
      first: 10,
      useTagLastActivityTime: false,
      useTagCounts: false
    }),
    [contextNodeId]
  );

  const { loading, data } = useQueryWithTracing<TopTagsQuery, TopTagsQueryVariables>(
    module,
    topTagsQuery,
    { variables, skip: tagsQueryFetchType === TagsQueryFetchType.FETCH_PRESET_TAGS }
  );

  const excludedTags = excludePresetTagsFromSuggestions
    ? [...tagsToExclude, ...presetTags]
    : tagsToExclude;

  const tagSuggestions =
    !loading && data?.coreNode?.topTags.edges
      ? data.coreNode.topTags.edges
          .map(tagNode => ({
            label: tagNode.node.text,
            value: tagNode.node.id
          }))
          .filter(
            suggestion =>
              !excludedTags.some(
                excludedTag => excludedTag.label.toLowerCase() === suggestion.label.toLowerCase()
              )
          )
      : [];

  /**
   * Fetches matching tags based on the provided input value.
   *
   * This function uses two different queries depending on whether the input value is empty or not:
   * - If the input value is empty or consists only of whitespace, it fetches the top tags using the `topTagsQuery` to return a list of suggestions a user can choose from.
   * - If the input value is non-empty, it fetches matching tags using the `tagSelectorQuery`.
   *
   * @param inputValue - The input string used to filter or search for tags.
   * @returns A promise that resolves to an array of `TagOption` objects.
   *
   * @remarks
   * - When no input is provided, the `topTagsQuery` is executed with variables including `contextNodeId`, `pageSize`, and flags for tag activity and counts.
   * - When input is provided, the `tagSelectorQuery` is executed with variables including `pageSize`, the input text, and a list of tags to exclude.
   *
   * @see `fetchTags` - The function used to execute the queries.
   * @see `topTagsQuery` - The query used to fetch top tags.
   * @see `tagSelectorQuery` - The query used to fetch matching tags based on input.
   */
  const fetchMatchingTags = useCallback(
    async (inputValue: string): Promise<TagOption[]> => {
      if (!inputValue) {
        // Call topTagsQuery when there is no input
        return fetchTags<TopTagsQuery, TopTagsQueryVariables>(topTagsQuery, variables);
      }

      // Call tagSelectorQuery when there is input
      const matchingTagVariables: TagSelectorQueryVariables = {
        first: pageSize,
        text: inputValue,
        excludedTags: {
          items: tagsToExclude?.map(tag => ({ text: tag.label })) ?? []
        }
      };
      return fetchTags<TagSelectorQuery, TagSelectorQueryVariables>(
        tagSelectorQuery,
        matchingTagVariables
      );
    },
    [pageSize, tagsToExclude, fetchTags, variables]
  );

  /**
   * Fetches the top tags based on the provided input value and context node ID. If an
   * input value is not provided, the top tags for that node are fetched as a suggestion.
   *
   * @param {string} [inputValue] - An optional input string to filter tags by text.
   *                                If provided and not empty, it will be used as a constraint.
   * @returns {Promise<TagOption[]>} - A promise that resolves to an array of tag options.
   *
   * @remarks
   * - The function uses the `fetchTags` utility to execute the query.
   * - By default, the query fetches the first 10 tags without considering tag activity
   *   time or tag counts.
   */
  const fetchTopTags = useCallback(
    async (inputValue?: string): Promise<TagOption[]> => {
      if (inputValue && inputValue.trim() !== '') {
        variables.constraints = {
          text: { eq: inputValue }
        };
      }

      return fetchTags<TopTagsQuery, TopTagsQueryVariables>(topTagsQuery, variables);
    },
    [fetchTags, variables]
  );

  /**
   * Callback used to fetch the preset tags
   */
  async function fetchPresetTags(inputValue: string): Promise<TagOption[]> {
    const presetTagsList = presetTags.filter(tag =>
      tag.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    return presetTagsList.sort((a, b) => a.label.localeCompare(b.label));
  }

  const getTagsFetchOptions = () => {
    switch (tagsQueryFetchType) {
      case TagsQueryFetchType.FETCH_PRESET_TAGS: {
        return fetchPresetTags;
      }
      case TagsQueryFetchType.FETCH_TOP_TAGS: {
        return fetchTopTags;
      }
      case TagsQueryFetchType.FETCH_MATCHING_TAGS: {
        return fetchMatchingTags;
      }
    }
  };

  function getDefaultOptions(): TagOption[] {
    switch (tagsQueryFetchType) {
      case TagsQueryFetchType.FETCH_PRESET_TAGS: {
        return presetTags.sort((a, b) => a.label.localeCompare(b.label));
      }
      default: {
        return tagSuggestions;
      }
    }
  }

  function renderCreateButton(input): React.ReactElement {
    return (
      <div className={cx('lia-tag-create')} data-testid="Add.New.Tag">
        <Icon icon={Icons.AddIcon} size={IconSize.PX_12} />
        <span className={cx('lia-tag-create-text')}>
          {formatMessage('inputCreate', {
            tag: input.toUpperCase()
          })}
        </span>
      </div>
    );
  }

  /**
   * We can work around the issue noted below by having
   * the input focus on the next-tick using a timeout.
   *
   * When the tag selector opens, Popper.js is still calculating the overlay's position,
   * and focusing the input field immediately causes the screen to shift downward
   * on the first click. The 100ms delay ensures Popper.js completes its calculations
   * before the input field is focused.
   */
  function focusSelect(): void {
    setTimeout(() => {
      reference?.current?.focus();
    }, 100);
  }

  useEffect(() => {
    /**
     * This code is added to set focus to the input field in the overlay.
     * Alternately, we can also use the autoFocus property in the Select field
     * but it causes the screen to shift to the bottom on the first click.
     * To avoid this behaviour, the below piece of code has been added
     */
    if (document.activeElement !== reference.current?.inputRef) {
      focusSelect();
    }
  });

  if (textLoading) {
    return null;
  }

  return (
    <AsyncCreatableSelect<CreatableTagOption, boolean>
      className={cx(
        'd-flex',
        popperPlacement === 'top-start' ? 'flex-column-reverse' : 'flex-column'
      )}
      classNamePrefix="lia-react-select"
      classNames={{
        control: state => (state.isFocused ? cx('lia-control-is-focused') : cx('lia-control')),
        valueContainer: () => cx('form-control'),
        menu: () => cx('lia-menu'),
        menuList: ({ selectProps }) =>
          cx('lia-menu-list', {
            'lia-menu-list-has-create-option': (selectProps?.options[0] as CreatableTagOption)
              ?.__isNew__
          })
      }}
      blurInputOnSelect={false}
      components={{
        IndicatorSeparator: (): null => null,
        DropdownIndicator: (): null => null,
        NoOptionsMessage: (): null => null,
        LoadingMessage: (props): React.ReactElement => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <span className={cx('sr-only')} role="alert" {...props.innerProps}>
            {props.children}
          </span>
        ),
        Option
      }}
      /**
       * When a user searches for a tag and selects one of the suggestions, the menu should close.
       * Keeping the menu open after a search is not ideal, as react-select does not provide a way
       * to remove the selected tag from dynamically generated suggestions.
       *
       * However, the menu will remain open for tag suggestions even when a tag is selected.
       */
      onInputChange={(_, actionMeta: InputActionMeta): void => {
        const { action, prevInputValue } = actionMeta;
        // Removed debugging console.log to prevent unintended logging
        if (action === 'menu-close' && prevInputValue !== '') {
          setMenuIsOpen(false);
        } else if (action === 'input-change' && prevInputValue !== '' && !menuIsOpen) {
          setMenuIsOpen(true);
        }
      }}
      cacheOptions={false}
      onChange={(values: MultiValue<TagOption>, action): void => {
        focusSelect();
        values?.map(value => {
          if ((value as TagOption).label.trim().length === 0) {
            return;
          }
          return onAdd(value, action);
        });
      }}
      onKeyDown={({ key }): void => {
        if (key === 'Escape') {
          onBlur();
        }
      }}
      placeholder={formatMessage('inputPlaceholder')}
      loadOptions={getTagsFetchOptions()}
      onBlur={onBlur}
      onFocus={() => {
        onFocus();
        setMenuIsOpen(true);
      }}
      createOptionPosition="first"
      formatCreateLabel={renderCreateButton}
      value={null}
      ref={reference}
      tabSelectsValue={false}
      theme={reactSelectTheme}
      menuIsOpen={menuIsOpen}
      /**
       * The `isMulti` prop works alongside the `defaultOptions` to enhance the user experience.
       * When the component gains focus, the menu remains open to show the list of tag suggestions.
       * Selecting a tag suggestion does not immediately close the menu, enabling users to choose
       * multiple tags. Selected tags are automatically removed from the list of suggestions,
       * which is handled internally by react-select when `isMulti` is set to true.
       */
      isMulti
      defaultOptions={getDefaultOptions()}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(!useUserDefinedTags && {
        isValidNewOption: inputValue => useUserDefinedTags && inputValue !== ''
      })}
    />
  );
};

export default TagSelector;
